import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import { applyThemeVars, resolveTheme } from './colors'
import {
  DEFAULT_THEME,
  type PresetKey,
  PRESETS,
  STORAGE_KEY,
  type Theme,
  type ThemeColors,
} from './themes'

interface ThemeApi {
  theme: Theme
  setPreset: (key: Exclude<PresetKey, 'custom'>) => void
  setColor: (key: keyof ThemeColors, value: string) => void
  reset: () => void
}

const ThemeContext = createContext<ThemeApi | null>(null)

export function useTheme(): ThemeApi {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return ctx
}

function persist(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme))
  } catch {
    // Storage disabled/full — theme still applies for the session.
  }
}

function readInitial(): Theme {
  try {
    return resolveTheme(localStorage.getItem(STORAGE_KEY))
  } catch {
    return DEFAULT_THEME
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // Lazy initializer resolves + applies before first paint.
  const [theme, setTheme] = useState<Theme>(() => {
    const initial = readInitial()
    applyThemeVars(initial.colors)
    return initial
  })

  const commit = useCallback((next: Theme) => {
    applyThemeVars(next.colors)
    persist(next)
    setTheme(next)
  }, [])

  const setPreset = useCallback(
    (key: Exclude<PresetKey, 'custom'>) => {
      const preset = PRESETS.find((p) => p.key === key)
      if (!preset) return
      commit({ presetKey: key, colors: preset.colors })
    },
    [commit],
  )

  const setColor = useCallback((key: keyof ThemeColors, value: string) => {
    setTheme((prev) => {
      const next: Theme = {
        presetKey: 'custom',
        colors: { ...prev.colors, [key]: value },
      }
      applyThemeVars(next.colors)
      persist(next)
      return next
    })
  }, [])

  const reset = useCallback(() => commit(DEFAULT_THEME), [commit])

  const api = useMemo<ThemeApi>(
    () => ({ theme, setPreset, setColor, reset }),
    [theme, setPreset, setColor, reset],
  )

  return <ThemeContext.Provider value={api}>{children}</ThemeContext.Provider>
}
